import React, { type HTMLInputTypeAttribute } from 'react'
import { css } from 'styled-components'
import type { FieldAria } from 'react-aria'

import { Flex } from '../Flex'
import { FormLabel, RequiredWrapper, type FormInputSize } from '../FormInput'
import type { InputLabelVariant } from '../FormInput/BaseInput'
import { CustomStyleOverrideProps } from '../FormInput/types'
import { Icon } from '../Icon'
import { Tooltip } from '../Tooltip'
import { TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip'
import { Typography } from '../Typography'
import { getVariantStyles } from '../Typography/Typography'
import { getDangerColor, getTextSecondaryColor } from '../FormInput/utils'

export type FieldMetadataProps = Omit<FieldAria, 'fieldProps'> & {
  children?: React.ReactNode
  className?: string
  label?: string
  errorMessage?: string
  tooltip?: string | React.ReactNode
  description?: string | React.ReactNode
  style?: React.CSSProperties
  size?: FormInputSize
  type?: HTMLInputTypeAttribute
  labelVariant?: InputLabelVariant
  required?: boolean
  maxLength?: number
  characterCount?: number
  baseOverrides?: Partial<CustomStyleOverrideProps>
}

function FieldMetadata(
  {
    children,
    className,
    labelProps,
    descriptionProps,
    errorMessageProps,
    label,
    tooltip,
    description,
    errorMessage,
    style,
    size,
    type,
    labelVariant,
    maxLength,
    characterCount,
    baseOverrides,
    ...props
  }: FieldMetadataProps,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <div
      {...props}
      className={className}
      style={style}
      ref={ref}
      css={
        type === 'hidden'
          ? 'display: none;'
          : labelVariant === 'inset'
            ? css`
              position: relative;
              label {
                position: absolute;
                top: 6px;
                left: 15px;
                font-weight: 400;
                ${getVariantStyles('body-xs')}
                z-index: 1;
              }
            `
            : undefined
      }
    >
      {label ? (
        <Flex align="center">
          <RequiredWrapper required={props.required} baseOverrides={baseOverrides}>
            <FormLabel
              {...(labelProps ? labelProps : {})}
              as='label'
              variant={size === 'small' ? 'label-sm' : 'label'}
              css={css`
                display: block;
                margin-bottom: ${size === 'small' ? 4 : 5}px;
              `}
              baseOverrides={baseOverrides}
            >
              {label}
            </FormLabel>
          </RequiredWrapper>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Icon
                    type='HELP'
                    color={
                      labelProps?.style?.color ||
                      getTextSecondaryColor(baseOverrides)
                    }
                    height={size === 'small' ? 16 : 20}
                    width={size === 'small' ? 16 : 20}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          )}
        </Flex>
      ) : null}
      {children}
      {errorMessage?.trim() ? (
        <Typography
          {...errorMessageProps}
          variant='body-sm'
          css={css`
            color: ${getDangerColor(baseOverrides)};
            margin-top: ${size === 'small' ? 2 : 4}px;
          `}
        >
          {errorMessage}
        </Typography>
      ) : null}
      {description ? (
        <Typography
          {...descriptionProps}
          variant='body-sm'
          css={css`
            margin-top: ${size === 'small' ? 2 : 4}px;
          `}
        >
          {description}
        </Typography>
      ) : null}
      {maxLength && (
        <Typography
          variant='body-sm'
          css={css`
            margin-top: ${size === 'small' ? 2 : 4}px;
          `}
        >
          {`${maxLength - (characterCount ?? 0)} / ${maxLength} characters left`}
        </Typography>
      )}
    </div>
  )
}

const _FieldMetadata = React.forwardRef(
  FieldMetadata
) as React.ForwardRefExoticComponent<
  Omit<FieldMetadataProps, 'ref'> & React.RefAttributes<HTMLDivElement>
>

export { _FieldMetadata as FieldMetadata }
